import { ArrayHelpers, FieldArray, Form, Formik, FormikProps } from 'formik';
import { Box, Paper, Stack } from '@mui/material';
import { ConfirmationDialog } from '@synergy/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolFilterSearchBar, PoolFormattedMessage } from '@synergy/components-next';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    ExternalCustomerMapping,
    FormValues,
} from '../../../components/external-customer-mapping/external-customer-mapping';
import { ExternalCustomerMappingAddEntry } from '../../../components/external-customer-mapping/external-customer-mapping-add-entry';
import { ExternalCustomerMappingTable } from '../../../components/external-customer-mapping/external-customer-mapping-table';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { Supplier } from '../../../../../common/models/supplier';
import {
    deleteExternalCustomerMapping,
    getExternalCustomerMappings,
    updateStoreExternalCustomerMapping,
} from './store/supplier-external-customer-mapping-actions';
import {
    externalCustomerMappingBaselineSelector,
    externalCustomerMappingSelector,
} from './store/supplier-external-customer-mapping-selectors';
import { externalCustomerMappingValidationSchema } from '../../../components/external-customer-mapping/external-customer-mapping-validation-schema';
import { filterExternalCustomerMappings } from '../../../utils/filter-external-customer-mappings';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const SupplierDetailExternalCustomerMapping = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const { formValues, errors, touched } = useAppSelector(externalCustomerMappingSelector);
    // The mappings as the server has them — see PaloDirtyComparison.
    const baseline = useAppSelector(externalCustomerMappingBaselineSelector);
    const [supplierExternalCustomerMapping, setSupplierExternalCustomerMapping] = useState<FormValues | null>(
        formValues
    );
    const [search, setSearch] = useState('');
    const [deleteModalData, setDeleteModalData] = useState<ExternalCustomerMapping | null>(null);

    const formikRef = useRef<FormikProps<FormValues>>(null);

    useEffect(() => {
        if (supplierIdParam) {
            dispatch(getExternalCustomerMappings({ supplierId: +supplierIdParam }));
        }
    }, [supplierIdParam, dispatch]);

    useEffect(() => {
        setSupplierExternalCustomerMapping(filterExternalCustomerMappings(formValues, search));
    }, [formValues, search]);

    const handleDeleteMapping = (arrayHelpers: ArrayHelpers, values: FormValues) => {
        if (deleteModalData && deleteModalData?.id < 1) {
            const index = values.mappings.findIndex(item => item.id === deleteModalData?.id);
            arrayHelpers.remove(index);
        } else if (supplierIdParam && deleteModalData) {
            dispatch(
                deleteExternalCustomerMapping({
                    supplierId: +supplierIdParam,
                    externalCustomerMappingId: deleteModalData.id,
                })
            );
        }
        setDeleteModalData(null);
    };

    const handleAddExternalCustomerMapping = (account: Supplier, arrayHelpers: ArrayHelpers, values: FormValues) => {
        const tempIndex = -1 * (values?.mappings.length ?? 0 + 1);
        arrayHelpers.push({ id: tempIndex, accountId: account.id, accountName: account.name, externalCustomerId: '' });
    };

    useFormikSubPage(ref, formikRef, async (values, isDirty, isValid, errors) => {
        dispatch(updateStoreExternalCustomerMapping({ formData: values, isDirty, isValid, errors }));
    });

    if (!supplierExternalCustomerMapping) {
        return null;
    }

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            validationSchema={externalCustomerMappingValidationSchema}
            initialValues={supplierExternalCustomerMapping}
            innerRef={formikRef}
            initialErrors={errors}
            initialTouched={touched}
            enableReinitialize={true}
        >
            {(formikProps: FormikProps<FormValues>) => (
                <Form>
                    <PaloDirtyComparison baseline={baseline} />
                    <FieldArray name="mappings">
                        {(arrayHelpers: ArrayHelpers) => (
                            <>
                                <Stack
                                    justifyContent="space-between"
                                    alignItems="center"
                                    flexDirection="row"
                                    sx={{ marginBottom: 3 }}
                                >
                                    <ExternalCustomerMappingAddEntry
                                        onAddMapping={value =>
                                            handleAddExternalCustomerMapping(value, arrayHelpers, formikProps.values)
                                        }
                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        id={supplierIdParam!}
                                        isCustomer={true}
                                    />
                                    <PoolFilterSearchBar
                                        onChange={setSearch}
                                        placeholder={intl.formatMessage({
                                            id: 'app.common.searchPlaceholder',
                                        })}
                                        onKeyDown={e => e.stopPropagation()}
                                        value={search}
                                        sx={{ minWidth: 230, width: 230 }}
                                        backgroundColor="transparent"
                                    />
                                </Stack>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        height: '75vh',
                                        backgroundColor: theme => theme.palette.background.default,
                                    }}
                                >
                                    <ExternalCustomerMappingTable
                                        isCustomer={true}
                                        externalCustomerMappings={formikProps.values}
                                        onDeleteMapping={mapping => setDeleteModalData(mapping)}
                                    />
                                </Paper>

                                <ConfirmationDialog
                                    isVisible={!!deleteModalData}
                                    title={
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.deleteExternalCustomerMapping.title" />
                                    }
                                    action={<FormattedMessage id="app.common.delete" />}
                                    onConfirm={() => handleDeleteMapping(arrayHelpers, formikProps.values)}
                                    onCancel={() => setDeleteModalData(null)}
                                >
                                    <Box textAlign={'center'}>
                                        <PoolFormattedMessage
                                            id="app.contactsAndOrganizations.detail.deleteSupplierExternalCustomerMapping.text"
                                            values={{ supplier: deleteModalData?.accountName }}
                                        />
                                    </Box>
                                </ConfirmationDialog>
                            </>
                        )}
                    </FieldArray>
                </Form>
            )}
        </Formik>
    );
});
